/**
 * useIdleTimeout
 *
 * Signs the user out after a configurable period of inactivity.
 * Tracks mouse, keyboard, touch, and scroll events to reset the timer.
 * Call once at the app root (App.tsx) — only runs while the user is signed in.
 *
 * Default: 30 minutes.
 */

import { useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'

const IDLE_MS = 30 * 60 * 1000 // 30 minutes
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const

export function useIdleTimeout(idleMs = IDLE_MS) {
  const { user, signOut } = useAuth()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!user) return // Only run when signed in

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        signOut('session_expired')
      }, idleMs)
    }

    // Start the timer immediately
    reset()

    // Reset on any user activity
    EVENTS.forEach(e => window.addEventListener(e, reset, { passive: true }))

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      EVENTS.forEach(e => window.removeEventListener(e, reset))
    }
  }, [user, idleMs, signOut])
}
